import { type CSSProperties } from "react";
import { useNavigate } from "react-router";
import { ActivityFeedItem, Card, EmptyState } from "../primitives";
import type { Persona } from "../primitives";
import { agentPersonaFromId } from "../../lib/agent-persona";
import { useListActivity } from "../../hooks/use-mcp-list-activity";
import { useSseActivity } from "../../hooks/use-sse-activity";

function formatRelativeTime(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diff / 60_000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}

interface RecentActivityProps {
  nodeId: string;
}

export function RecentActivity({ nodeId }: RecentActivityProps) {
  const navigate = useNavigate();
  const activity = useListActivity({ agentId: nodeId, limit: 8 });
  const items = activity.data?.items ?? [];

  useSseActivity({ agentId: nodeId });

  const heading: CSSProperties = {
    fontSize: 14,
    fontWeight: 500,
    color: "var(--color-text-primary)",
    marginBottom: 4,
  };

  if (activity.isLoading) {
    return (
      <Card>
        <div style={heading}>Recent activity</div>
        <div style={{ fontSize: 13, color: "var(--color-text-tertiary)", padding: "12px 0" }}>
          Loading...
        </div>
      </Card>
    );
  }

  return (
    <Card>
      <div style={heading}>Recent activity</div>
      {items.length === 0 ? (
        <EmptyState>
          No activity yet — ask Claude or another tool to do something with this agent.
        </EmptyState>
      ) : (
        <div>
          {items.map((item) => (
            <ActivityFeedItem
              key={item.id}
              agent={{
                id: item.agent.id,
                name: item.agent.name,
                persona: (item.agent.persona as Persona) || agentPersonaFromId(item.agent.id),
                role: item.agent.role,
              }}
              surface={item.surface}
              body={item.body}
              timestamp={formatRelativeTime(item.timestamp)}
              qualifier={item.qualifier}
            />
          ))}
          <div
            style={{
              fontSize: 12,
              color: "var(--color-text-info)",
              cursor: "pointer",
              padding: "8px 0 0",
              display: "flex",
              alignItems: "center",
              gap: 4,
            }}
            onClick={() => navigate(`/activity?agentId=${nodeId}`)}
          >
            Open full activity
            <i className="ti ti-arrow-right" style={{ fontSize: 12 }} aria-hidden="true" />
          </div>
        </div>
      )}
    </Card>
  );
}
